import { DialogTitle } from '@headlessui/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ChipsAutocomplete from '@betterfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { DynamicPage, DynamicPageTag } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import AnalyticsRangeSelector from '../components/AnalyticsRangeSelector';
import { AnalyticsRange, DashboardContext } from '../contexts/DashboardContext';
import { useExportAnalyticsContext } from '../contexts/ExportAnalyticsContext';
import { UserContext } from '../contexts/UserContext';
import { analyticsRangeToDates } from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ExportAnalyticsModal({ open, onClose }: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { analyticsRange: defalutAnalyticsRange, dynamicPages: campaigns } =
    useContext(DashboardContext);
  const { exportAnalytics } = useExportAnalyticsContext();

  const campaignTags = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    campaigns?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values());
  }, [campaigns]);

  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>(
    defalutAnalyticsRange,
  );
  const [selectedDynamicPages, setSelectedDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [selectedDynamicPageTags, setSelectedDynamicPageTags] = useState<
    DynamicPageTag[]
  >([]);
  const [metrics, setMetrics] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [segments, setSegments] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();

  const [freeTextGroupBy, setFreeTextGroupBy] = useState<string>('');

  const [exported, setExported] = useState<{ downloadUrl: string }>();

  useEffect(() => {
    setAnalyticsRange(defalutAnalyticsRange);
    setSelectedDynamicPages(undefined);
    setSelectedDynamicPageTags([]);
    setMetrics([
      'uniqueVisitors',
      'uniqueConversions',
      'campaignConversionRate',
    ]);
    setGroupBy([]);
    setSegments([]);
    setCreating(false);
    setError(undefined);
    setExported(undefined);
  }, [defalutAnalyticsRange, open]);

  const metricsOptions = useMemo(() => {
    return [
      { value: 'uniqueVisitors', title: 'Visitors' },
      { value: 'uniqueConversions', title: 'Conversions' },
      { value: 'uniqueButtonClicks', title: 'Button Clicks' },
      {
        value: 'campaignConversionRate',
        title: 'Landing Page Conversion Rate',
      },
    ];
  }, []);

  const groupByOptions = useMemo(() => {
    return [
      { value: 'campaignId', title: 'Dynamic Page' },
      { value: 'variantId', title: 'Variant' },
      { value: 'websiteId', title: 'Test' },
      { value: 'device', title: 'Device' },
      { value: 'extra.buttonLink', title: 'Button Link' },
    ];
  }, []);

  const segmentsOptions = useMemo(() => {
    return [{ value: 'day', title: 'Day' }];
  }, []);

  const onExportAnalyticsClick = useCallback(async () => {
    if (!selectedAccount) return;

    setCreating(true);

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    try {
      const campaignTagIdsSet = new Set(
        selectedDynamicPageTags.map((t) => t.id),
      );
      const campaignIdsFromTags =
        campaigns
          ?.filter((c) =>
            c.data().tags?.some((t) => campaignTagIdsSet.has(t.id)),
          )
          .map((c) => c.id) ?? [];

      await exportAnalytics(
        selectedAccount.id,
        selectedDynamicPages
          ? {
              campaignId: {
                $in: Array.from(
                  new Set(
                    selectedDynamicPages
                      .map((c) => c.id)
                      .concat(campaignIdsFromTags),
                  ),
                ),
              },
            }
          : {},
        metrics.map((m) => metricsOptions.find((e) => e.value === m) as any),
        groupBy.map(
          (m) =>
            groupByOptions.find((e) => e.value === m) || { value: m, title: m },
        ),
        segments.map((m) => segmentsOptions.find((e) => e.value === m) as any),
        startDate,
        endDate,
      );
      // setExported(res);
      onClose();
      setError(undefined);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setCreating(false);
    }
  }, [
    selectedAccount,
    analyticsRange,
    selectedDynamicPageTags,
    campaigns,
    exportAnalytics,
    selectedDynamicPages,
    metrics,
    groupBy,
    segments,
    onClose,
    metricsOptions,
    groupByOptions,
    segmentsOptions,
  ]);

  const downloadReport = useCallback(async (fileUrl: string) => {
    try {
      window.open(fileUrl, '_blank');
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error downloading the file: ', e);
    }
  }, []);

  const actions: ModalActionData[] = useMemo(
    () =>
      exported
        ? [
            {
              title: 'Close',
              type: 'secondary',
              onClick: () => onClose(),
            },
            { type: 'spacer' },
            {
              title: 'Download',
              type: 'primary',
              onClick: () => downloadReport(exported.downloadUrl),
            },
          ]
        : [
            {
              title: 'Cancel',
              type: 'secondary',
              disabled: creating,
              onClick: () => onClose(),
            },
            {
              title: 'Export',
              type: 'primary',
              loading: creating,
              onClick: () => onExportAnalyticsClick(),
            },
          ],
    [creating, downloadReport, exported, onClose, onExportAnalyticsClick],
  );

  return (
    <Modal
      open={open}
      onClose={creating ? () => {} : onClose}
      actionsAlignment="end"
      actions={actions}
      width="xl"
    >
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Export Analytics
      </DialogTitle>
      <div className="mt-4 flex min-h-[430px] flex-col gap-4">
        <AnalyticsRangeSelector
          size="normal"
          analyticsRange={analyticsRange}
          setAnalyticsRange={setAnalyticsRange}
          disabled={creating || !!exported}
        />

        <ChipsAutocomplete<string>
          title="Metrics"
          options={metricsOptions.map((e) => e.value)}
          getValue={(c) =>
            metricsOptions.find((e) => e.value === c)?.title ?? c
          }
          selected={metrics}
          onChange={setMetrics}
          disabled={creating || !!exported}
          className="w-full"
        />

        <ChipsAutocomplete<string>
          title="Group By"
          onInputChange={setFreeTextGroupBy}
          options={[
            ...(freeTextGroupBy ? [freeTextGroupBy] : []),
            ...groupBy.filter((g) => !groupByOptions.map((e) => e.value !== g)),
            ...groupByOptions.map((e) => e.value),
          ]}
          getValue={(c) =>
            groupByOptions.find((e) => e.value === c)?.title ?? c
          }
          selected={groupBy}
          disabled={creating || !!exported}
          onChange={setGroupBy}
          className="w-full"
        />

        <ChipsAutocomplete<string>
          title="Segments"
          options={[
            ...segments.filter(
              (g) => !segmentsOptions.map((e) => e.value !== g),
            ),
            ...segmentsOptions.map((e) => e.value),
          ]}
          getValue={(c) =>
            segmentsOptions.find((e) => e.value === c)?.title ?? c
          }
          selected={segments}
          disabled={creating || !!exported}
          onChange={setSegments}
          className="w-full"
        />

        <Toggle
          label={'Export All Dynamic Pages'}
          checked={!selectedDynamicPages}
          disabled={creating || !!exported}
          onChange={(checked) => {
            setSelectedDynamicPages(checked ? undefined : []);
          }}
        />
        {selectedDynamicPages && (
          <>
            <ChipsAutocomplete<QueryDocumentSnapshot<DynamicPage, DynamicPage>>
              title="Dynamic Pages"
              options={campaigns || []}
              getOptionId={(c) => c.id}
              getValue={(c) => c.data().name}
              selected={selectedDynamicPages || []}
              onChange={setSelectedDynamicPages}
              className="w-full"
              disabled={creating || !!exported || !selectedDynamicPages}
            />
            <ChipsAutocomplete<DynamicPageTag>
              title="Tags"
              options={campaignTags || []}
              getOptionId={(c) => c.id}
              getValue={(c) => c.name}
              selected={selectedDynamicPageTags}
              onChange={setSelectedDynamicPageTags}
              className="w-full"
              disabled={creating || !!exported || !selectedDynamicPages}
            />
          </>
        )}
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create form'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
